import type { QueryClient } from '@tanstack/react-query';
import { subscriptionTopicKeys, type SubscriptionTopic } from './entity';

export function setSubscriptionTopicCache(qc: QueryClient, workspaceId: number, topic: SubscriptionTopic): void {
  qc.setQueryData(subscriptionTopicKeys.detail(workspaceId, topic.id), topic);
}

export function getSubscriptionTopicCache(
  qc: QueryClient,
  workspaceId: number,
  id: string,
): SubscriptionTopic | undefined {
  return qc.getQueryData<SubscriptionTopic>(subscriptionTopicKeys.detail(workspaceId, id));
}

export function removeSubscriptionTopicCache(qc: QueryClient, workspaceId: number, id: string): void {
  qc.removeQueries({ queryKey: subscriptionTopicKeys.detail(workspaceId, id) });
}

export function invalidateSubscriptionTopicLists(qc: QueryClient, workspaceId: number): void {
  void qc.invalidateQueries({ queryKey: subscriptionTopicKeys.lists(workspaceId) });
}

/**
 * Patch an edited topic into its detail slot and every cached list row.
 *
 * The update response echoes the edited fields but recomputes `usage` from the
 * transaction that just ran, so a topic whose usage the edit cannot have
 * changed would otherwise flicker between the cached and the echoed value.
 * Keeping the cached `usage` is what makes the delete guard and the usage
 * summary stable across an edit.
 */
export function setUpdatedSubscriptionTopicCache(qc: QueryClient, workspaceId: number, topic: SubscriptionTopic): void {
  const cachedUsage = getSubscriptionTopicCache(qc, workspaceId, topic.id)?.usage;
  const merged: SubscriptionTopic = cachedUsage == null ? topic : { ...topic, usage: cachedUsage };

  setSubscriptionTopicCache(qc, workspaceId, merged);
  qc.setQueriesData<SubscriptionTopic[]>({ queryKey: subscriptionTopicKeys.lists(workspaceId) }, (previous) =>
    previous?.map((row) => (row.id === merged.id ? { ...merged, usage: row.usage ?? merged.usage } : row)),
  );
}
